import React, { useState, useEffect } from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import {useDocTitle} from '../components/CustomHook';
import emailjs from '@emailjs/browser';
import Notiflix from 'notiflix';

const DemoProduct = () => {
    useDocTitle('NexAI Solution | Request Demo')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [service, setService] = useState('')
    const [message, setMessage] = useState('')
    const [errors, setErrors] = useState({})
    const [isSending, setIsSending] = useState(false)

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const validateForm = () => {
        let tempErrors = {}
        let isValid = true

        if (!firstName.trim()) {
            tempErrors.firstName = 'First name is required'
            isValid = false
        }

        if (!lastName.trim()) {
            tempErrors.lastName = 'Last name is required'
            isValid = false
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!email) {
            tempErrors.email = 'Email is required'
            isValid = false
        } else if (!emailRegex.test(email)) {
            tempErrors.email = 'Please enter a valid email'
            isValid = false
        }

        const phoneRegex = /^\d{10}$/
        if (!phone) {
            tempErrors.phone = 'Phone number is required'
            isValid = false
        } else if (!phoneRegex.test(phone)) {
            tempErrors.phone = 'Please enter a valid 10-digit phone number'
            isValid = false
        }

        if (!service) {
            tempErrors.service = 'Please select a service'
            isValid = false
        }

        if (!message.trim()) {
            tempErrors.message = 'Message is required'
            isValid = false
        } else if (message.trim().length < 10) {
            tempErrors.message = 'Message should be at least 10 characters'
            isValid = false
        }

        setErrors(tempErrors)
        return isValid
    }

    const clearErrors = () => {
        setErrors({})
    }

    const clearInput = () => {
        setFirstName('')
        setLastName('')
        setEmail('')
        setPhone('')
        setService('')
        setMessage('')
        setErrors({})
    }

    const showSuccessDialog = () => {
        Notiflix.Confirm.show(
            'Demo Request Submitted Successfully! 🎉',
            'Thank you for your interest! Our team will review your request and contact you soon to schedule a demo.',
            'Close',
            'Request Another Demo',
            () => {
                // User clicked Close - do nothing
            },
            () => {
                // User clicked Request Another Demo - clear the form
                clearInput();
            },
            {
                titleColor: '#1e40af',
                okButtonBackground: '#1e40af', 
                cancelButtonBackground: '#60a5fa', 
                borderRadius: '8px',
                width: '400px',
                messageColor: '#374151',
                buttonColor: '#ffffff'
            }
        );
    };

    const sendEmail = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            Notiflix.Report.failure(
                'Validation Error',
                'Please check all required fields',
                'Okay'
            );
            return;
        }

        setIsSending(true);

        try {
            const templateParams = {
                user_name: `${firstName} ${lastName}`,
                user_email: email,
                phone_number: phone,
                service: service,
                message: message,
                request_type: 'Demo Request',
                subject: 'Demo Request Submission'
            };

            await emailjs.send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_EMAILJS_DEMO_TEMPLATE_ID,
                templateParams,
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY
            );

            clearInput();
            showSuccessDialog();
        } catch (error) {
            console.error('Failed to send email:', error);
            Notiflix.Report.failure(
                'Error',
                'Failed to send message. Please try again later.',
                'Okay'
            );
        } finally {
            setIsSending(false);
        }
    }

    return (
        <>
            <div>
                <NavBar />
            </div>
            
            <div className="flex flex-col justify-center items-center mt-8 w-full bg-white py-12 lg:py-24">
                <div className="container mx-auto px-4 lg:px-20" data-aos="zoom-in">
                    <h1 className="text-5xl font-bold text-center text-blue-900 mb-6">Demo Our Services</h1>
                    <div className='flex justify-center mb-12'>
                        <div className='w-24 border-b-4 border-blue-900'></div>
                    </div>
                    <form onSubmit={sendEmail}>
                        <div className="flex flex-wrap -mx-3 mb-6">
                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                                    htmlFor="grid-first-name">
                                    First Name
                                </label>
                                <input
                                    className={`appearance-none block w-full bg-gray-200 text-gray-700 border ${errors.firstName ? "border-red-500" : "border-gray-200"} rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white`}
                                    id="grid-first-name"
                                    type="text"
                                    placeholder="Jane"
                                    value={firstName}
                                    onChange={(e) => {setFirstName(e.target.value); clearErrors()}}
                                />
                                {errors.firstName && <p className="text-red-500 text-xs italic">{errors.firstName}</p>}
                            </div>
                            <div className="w-full md:w-1/2 px-3">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                                    htmlFor="grid-last-name">
                                    Last Name
                                </label>
                                <input
                                    className={`appearance-none block w-full bg-gray-200 text-gray-700 border ${errors.lastName ? "border-red-500" : "border-gray-200"} rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500`}
                                    id="grid-last-name"
                                    type="text"
                                    placeholder="Doe"
                                    value={lastName}
                                    onChange={(e) => {setLastName(e.target.value); clearErrors()}}
                                />
                                {errors.lastName && <p className="text-red-500 text-xs italic">{errors.lastName}</p>}
                            </div>
                        </div>
                        <div className="flex flex-wrap -mx-3 mb-6">
                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                                    htmlFor="grid-email">
                                    Email Address
                                </label>
                                <input
                                    className={`appearance-none block w-full bg-gray-200 text-gray-700 border ${errors.email ? "border-red-500" : "border-gray-200"} rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white`}
                                    id="grid-email"
                                    type="email"
                                    placeholder="jane@example.com"
                                    value={email}
                                    onChange={(e) => {setEmail(e.target.value); clearErrors()}}
                                />
                                {errors.email && <p className="text-red-500 text-xs italic">{errors.email}</p>}
                            </div>
                            <div className="w-full md:w-1/2 px-3">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                                    htmlFor="grid-phone">
                                    Phone Number
                                </label>
                                <input
                                    className={`appearance-none block w-full bg-gray-200 text-gray-700 border ${errors.phone ? "border-red-500" : "border-gray-200"} rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500`}
                                    id="grid-phone"
                                    type="text"
                                    placeholder="1234567890"
                                    value={phone}
                                    onChange={(e) => {setPhone(e.target.value); clearErrors()}}
                                />
                                {errors.phone && <p className="text-red-500 text-xs italic">{errors.phone}</p>}
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-3 mb-6">
                            <div className="w-full px-3">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                                    htmlFor="grid-service">
                                    Service Interested In
                                </label>
                                <select
                                    className={`appearance-none block w-full bg-gray-200 text-gray-700 border ${errors.service ? "border-red-500" : "border-gray-200"} rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500`}
                                    id="grid-service"
                                    value={service}
                                    onChange={(e) => {setService(e.target.value); clearErrors()}}
                                >
                                    <option value="">Select a service</option>
                                    <option value="AI Solutions">AI Solutions</option>
                                    <option value="Software Development">Software Development</option>
                                    <option value="Cloud & DevOps">Cloud & DevOps</option>
                                    <option value="IT Consulting">IT Consulting</option>
                                </select>
                                {errors.service && <p className="text-red-500 text-xs italic">{errors.service}</p>}
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-3 mb-6">
                            <div className="w-full px-3">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                                    htmlFor="grid-message">
                                    Your Message
                                </label>
                                <textarea
                                    rows="10"
                                    className={`appearance-none block w-full bg-gray-200 text-gray-700 border ${errors.message ? "border-red-500" : "border-gray-200"} rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500`}
                                    placeholder="Type your message here..."
                                    value={message}
                                    onChange={(e) => {setMessage(e.target.value); clearErrors()}}
                                />
                                {errors.message && <p className="text-red-500 text-xs italic">{errors.message}</p>}
                            </div>
                            <div className="flex justify-between w-full px-3">
                                <button
                                    type="submit"
                                    className={`shadow bg-blue-900 hover:bg-blue-800 focus:shadow-outline focus:outline-none text-white font-bold py-2 px-6 rounded ${isSending ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    disabled={isSending}
                                >
                                    {isSending ? 'Sending...' : 'Request Demo'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            <Footer />
        </>
    )
}

export default DemoProduct;